// @flow
import React, { type Node, useEffect, useState } from 'react';
import { colors, fontWeight, buildVersionSelector } from '../constants';
import { createGlobalStyle, withTheme } from 'styled-components';
import { Steps } from 'intro.js-react';
import { darken } from 'polished';
import { XIcon } from '../icons';
import { renderToString } from 'react-dom/server';
import { BodyText } from '../typography';
import type { ReskinThemeType } from '../constants';
import 'intro.js/introjs.css';

type Step = {
  element: string,
  title: Node,
  description: string | Node,
  position?: string,
};

export type GuidancePopoverProps = {
  announcementKey: string,
  steps: Array<Step>,
  app: 'ghr' | 'gho',
  modalGuidance?: boolean,
  theme?: { name: ReskinThemeType },
};

export type GuidancePopoverState = {
  enabled: boolean,
};

const ALWAYS_DISMISSED = 'guidance_popover/all';

const azaleaVersion = {
  button: {
    borderRadius: '0px',
    color: colors.mediumGreenhouseGreen,
  },
};

const birchVersion = {
  button: {
    borderRadius: '25px',
    color: colors.mediumBlue,
  },
};

export function preventAllGuidancePopovers() {
  if (localStorage) {
    localStorage.setItem(ALWAYS_DISMISSED, 'true');
  }
}

export function allowGuidancePopovers() {
  if (localStorage) {
    localStorage.removeItem(ALWAYS_DISMISSED);
  }
}

export function guidancePopoversDisabled() {
  return localStorage && localStorage.getItem(ALWAYS_DISMISSED) === 'true';
}

export function restoreDismissedGuidancePopovers() {
  if (!localStorage) {
    return;
  }

  const keysToRemove = Object.keys(localStorage).filter(
    (key) => key.startsWith('guidance_popover/') && key !== ALWAYS_DISMISSED
  );

  keysToRemove.forEach((key) => localStorage.removeItem(key));
}

export function useGuidancePopoverState() {
  const [guidancePopoversEnabled, setGuidancePopoversEnabled] =
    useState<boolean>(!guidancePopoversDisabled());

  useEffect(() => {
    if (guidancePopoversEnabled) {
      allowGuidancePopovers();
    } else {
      preventAllGuidancePopovers();
    }
  }, [guidancePopoversEnabled]);

  return [guidancePopoversEnabled, setGuidancePopoversEnabled];
}

class GuidancePopover extends React.Component<
  GuidancePopoverProps,
  GuidancePopoverState
> {
  static defaultProps = {
    app: 'ghr',
  };

  constructor(props: GuidancePopoverProps) {
    super(props);

    this.state = {
      enabled: false,
    };
  }

  componentDidMount() {
    if (this.previouslyDismissed() || guidancePopoversDisabled()) {
      return;
    }

    this.preventScrollOnPageLoad();
    this.setState({ enabled: true });
  }

  render() {
    const GlobalStyles = this.globalStyles();

    // See https://www.npmjs.com/package/intro.js-react#steps for documentation
    // on using the <Steps> component.
    return (
      <>
        <GlobalStyles />
        <Steps
          enabled={this.state.enabled && !!this.props.announcementKey}
          options={this.options()}
          steps={this.steps()}
          initialStep={0}
          onExit={this.dismiss}
          onAfterChange={this.registerClose}
        />
      </>
    );
  }

  preventScrollOnPageLoad() {
    history.scrollRestoration = 'manual';
  }

  registerClose = () => {
    // The steps are rendered in plain text.
    // So need to register events on the close button
    const popover = document.getElementsByClassName('gh-guidance-popover');
    const that = this;

    if (popover.length > 0) {
      popover[0].addEventListener(
        'click',
        function (e: Event) {
          const target = e.target;
          const closeButton = document.querySelector(
            '.gh-guidance-popover .close'
          );
          if (
            target instanceof Element &&
            closeButton &&
            closeButton.contains(target)
          ) {
            that.dismiss();
          }
        },
        false
      );
    }
  };

  previouslyDismissed() {
    return (
      !localStorage || localStorage.getItem(this.localStorageKey()) === 'true'
    );
  }

  localStorageKey() {
    return 'guidance_popover/' + this.props.announcementKey;
  }

  dismiss = () => {
    localStorage.setItem(this.localStorageKey(), 'true');

    this.setState({ enabled: false });
  };

  options() {
    return {
      overlayOpacity: 0.5,
      showStepNumbers: false,
      showProgress: false,
      showBullets: false,
      doneLabel: 'Got it',
      nextLabel: 'Next',
      exitOnEsc: false,
      exitOnOverlayClick: false,
      hidePrev: true,
      tooltipClass: 'gh-guidance-popover',
      highlightClass: 'gh-guidance-popover-highlight',
    };
  }

  steps() {
    const totalPage = this.props.steps.length;

    //$FlowFixMe
    return this.props.steps.map((step, index) => {
      const currentPage = index + 1;

      return {
        element: step.element,
        position: step.position,
        intro: renderToString(
          <GuidanceStep
            title={step.title}
            description={step.description}
            currentPage={currentPage}
            totalPage={totalPage}
          />
        ),
      };
    });
  }

  globalStyles() {
    const versionSelector = buildVersionSelector(azaleaVersion, birchVersion);
    const appColor =
      this.props.app === 'ghr'
        ? versionSelector(this.props.theme?.name).button.color
        : colors.ghoBlue;

    const targetOpacity = this.props.modalGuidance ? 'opacity: 0' : '';

    return createGlobalStyle`
      @keyframes shadow-pulse
      {
        0% {
          box-shadow: 0 0 0 0px ${appColor};
        }
        100% {
          box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
        }
      }

      .introjs-tooltip.gh-guidance-popover {
        width: 340px;
        padding: 15px;
        font-size: 14px;
        box-shadow: 0 1px 10px rgba(0, 0, 0, .2);
        border-radius: 2px;

        header {
          font-weight: ${fontWeight.heavy};
          font-size: 14px;
          color: ${colors.darkestGrey};
          margin-top: 20px;
        }

        p {
          color: ${colors.darkestGrey};
          font-weight: ${fontWeight.normal};
          margin-top: 5px;
        }

        .page {
          position: absolute;
          bottom: 25px;
          color: ${colors.darkGrey};
          font-size: 12px;
        }

        svg {
          position: absolute;
          top: 10px;
          right: 10px;
        }

        .introjs-skipbutton, .introjs-prevbutton, .introjs-disabled {
          display: none;
        }

        .introjs-skipbutton.introjs-donebutton {
          display: inline-block;
        }

        .introjs-button {
          background: ${appColor};
          border: solid 1px ${appColor};
          padding: 8px 15px;
          width: 90px;
          text-align: center;
          color: ${colors.white};
          font-size: 13px;
          font-weight: ${fontWeight.heavy};
          text-shadow: none;
          font-family: 'Untitled Sans', sans-serif;
          border-radius: ${
            versionSelector(this.props.theme?.name).button.borderRadius
          };

          &:hover {
            background: ${darken(0.1, appColor)};
            color: ${colors.white};
            border-color: #008f87;
          }
        }

        .introjs-arrow {
          border: 7px solid ${colors.white};
        }

        .introjs-arrow.top {
          top: -14px;
          border-top-color: transparent;
          border-right-color: transparent;
          border-bottom-color: ${colors.white};
          border-left-color: transparent;
        }

        .introjs-arrow.top-right {
          top: -14px;
          right: 20px;
          border-top-color: transparent;
          border-right-color: transparent;
          border-bottom-color: ${colors.white};
          border-left-color: transparent;
        }

        .introjs-arrow.top-middle {
          top: -14px;
          margin-left: -10px;
          border-top-color: transparent;
          border-right-color: transparent;
          border-bottom-color: ${colors.white};
          border-left-color: transparent;
        }

        .introjs-arrow.left {
          left: -14px;
          top: 20px;
          border-top-color: transparent;
          border-right-color: ${colors.white};
          border-bottom-color: transparent;
          border-left-color: transparent;
        }

        .introjs-arrow.right {
          right: -14px;
          top: 20px;
          border-top-color: transparent;
          border-right-color: transparent;
          border-bottom-color: transparent;
          border-left-color: ${colors.white};
        }

        .introjs-arrow.bottom {
          bottom: -14px;
          border-top-color: ${colors.white};
          border-right-color: transparent;
          border-bottom-color: transparent;
          border-left-color: transparent;
        }
      }

      .introjs-helperLayer.gh-guidance-popover-highlight {
        border: none;
        box-shadow: none;
      }

      .introjs-overlay {
        z-index: 1001;
      }

      .introjs-showElement,
      tr.introjs-showElement > td,
      tr.introjs-showElement > th {
        animation: shadow-pulse 2s infinite;
        z-index: 1002 !important; // introjs.css is using !important for this selector
      }

      .introjs-helperLayer {
        z-index: 1001;
        ${targetOpacity};
      }

      .introjs-tooltipReferenceLayer {
        z-index: 1003;
      }
    `;
  }
}

export default withTheme(GuidancePopover);

type GuidanceStepProps = {
  title: Node,
  description: string | Node,
  currentPage: number,
  totalPage: number,
};

class GuidanceStep extends React.Component<GuidanceStepProps> {
  render() {
    const { title, description, currentPage, totalPage } = this.props;

    return (
      <React.Fragment>
        <header>{title}</header>

        <BodyText>{description}</BodyText>

        <span className="page">
          {currentPage}/{totalPage}
        </span>

        <div className="close">
          <XIcon className="close" />
        </div>
      </React.Fragment>
    );
  }
}
